<template>
  <div id="dashboard-analytics">
    <div class="vx-row">
      <div
        class="vx-col w-full lg:w-1/1 mb-base"
        v-if="startDate != null && endDate != null"
      >
        <vx-card title="" id="tabs-gráficas">
          <div id="bannerContainer" style="margin-bottom: 2rem;">
            <banner-clients />
          </div>

          <!-- ✅ Texto amigable sobre el filtro -->
          <div class="gw-filter-hint">
            <div class="gw-filter-hint__title">
              Filtra tu información por fechas y ubicación
            </div>
            <div class="gw-filter-hint__text">
              Selecciona un rango de fechas para ver información de interés sobre tus procesos y
              sobre las personas que están en tu proceso de contratación.
            </div>
          </div>

          <!-- Filtro principal (fechas + país/ciudad) -->
          <vx-card>
            <div class="vx-col w-full lg:w-3/4 mb-base">
              Total: {{ totalCount }} procesos

              <div class="vx-row">
                <div class="vx-col sm:w-1/4 w-full">
                  Fecha de inicio:
                  <datepicker
                    mode="date"
                    :language="languages[language]"
                    v-validate="'required'"
                    class="pickadate-limits"
                    name="dateStart"
                    v-model="startDateMask"
                  />
                  <span class="text-danger text-sm" v-show="errors.has('dateStart')">
                    Requerido
                  </span>
                </div>

                <div class="vx-col sm:w-1/4 w-full">
                  Fecha final:
                  <datepicker
                    mode="date"
                    :language="languages[language]"
                    v-validate="'required'"
                    class="pickadate-limits"
                    name="dateEnd"
                    v-model="endDateMask"
                  />
                  <span class="text-danger text-sm" v-show="errors.has('dateEnd')">
                    Requerido
                  </span>
                </div>

                <div class="vx-col sm:w-1/4 w-full">
                  País:
                  <v-select
                    :options="countryOptions"
                    :clearable="true"
                    :reduce="(o) => o.id"
                    label="label"
                    v-model="selectedCountryId"
                    placeholder="Selecciona un país"
                  />
                </div>

                <div class="vx-col sm:w-1/4 w-full">
                  Ciudad:
                  <v-select
                    :options="cityOptions"
                    :clearable="true"
                    :reduce="(o) => o.id"
                    label="label"
                    v-model="selectedCityId"
                    :disabled="selectedCountryId == null"
                    :placeholder="selectedCountryId ? 'Selecciona una ciudad' : 'Selecciona país primero'"
                  />
                </div>

                <div class="vx-col sm:w-1/4 w-full">
                  <br />
                  <vs-button type="filled" @click.prevent="consultData" class="block">
                    Consultar
                  </vs-button>
                </div>
              </div>
            </div>
          </vx-card>

          <br />

          <vs-tabs>
            <vs-tab label="Procesos">
              <div class="con-tab-ejemplo">
                <div class="vx-row">
                  <div
                    class="vx-col w-full lg:w-1/2 mb-base"
                    v-if="this.userInfo.role_name != 'Cliente'"
                  >
                    <pie-chart
                      :startDate="startDate"
                      :endDate="endDate"
                      :cityId="city_id"
                    />
                  </div>

                  <div class="vx-col w-full lg:w-1/2 mb-base">
                    <projects-by-advance
                      :startDate="startDate"
                      :endDate="endDate"
                      :cityId="city_id"
                    />
                  </div>
                </div>

                <div class="vx-row">
                  <div class="vx-col w-full lg:w-1/2 mb-base">
                    <qualification-average
                      :startDate="startDate"
                      :endDate="endDate"
                      :cityId="city_id"
                    />
                  </div>

                  <div class="vx-col w-full lg:w-1/2 mb-base">
                    <projects-by-results
                      :startDate="startDate"
                      :endDate="endDate"
                      :cityId="city_id"
                    />
                  </div>

                  <div class="vx-col w-full lg:w-1/2 mb-base">
                    <projects-by-type
                      :startDate="startDate"
                      :endDate="endDate"
                      :cityId="city_id"
                    />
                  </div>

                  <div class="vx-col w-full lg:w-1/2 mb-base">
                    <untrust-projects
                      :startDate="startDate"
                      :endDate="endDate"
                      :cityId="city_id"
                    />
                  </div>

                  <div class="vx-col w-full lg:w-1/2 mb-base">
                    <unreliable-projects
                      :startDate="startDate"
                      :endDate="endDate"
                      :cityId="city_id"
                    />
                  </div>

                  <div class="vx-col w-full lg:w-1/2 mb-base">
                    <projects-by-avg-completion-time-by-type
                      :startDate="startDate"
                      :endDate="endDate"
                      :cityId="city_id"
                    />
                  </div>
                </div>
              </div>
            </vs-tab>

            <vs-tab label="Candidatos">
              <div class="con-tab-ejemplo">
                <div class="vx-row">
                  <div class="vx-col lg:w-1/2 mb-base">
                    <candidate-gender-age
                      :startDate="startDate"
                      :endDate="endDate"
                      :cityId="city_id"
                    />
                  </div>

                  <div class="vx-col lg:w-1/2 mb-base">
                    <candidate-civil-state-gender
                      :startDate="startDate"
                      :endDate="endDate"
                      :cityId="city_id"
                    />
                  </div>
                </div>

                <div class="vx-row">
                  <div class="vx-col w-full lg:w-1/2 mb-base">
                    <candidate-age-result
                      :startDate="startDate"
                      :endDate="endDate"
                      :cityId="city_id"
                    />
                  </div>

                  <div class="vx-col w-full lg:w-1/2 mb-base">
                    <candidate-age-civil-state
                      :startDate="startDate"
                      :endDate="endDate"
                      :cityId="city_id"
                    />
                  </div>
                </div>

                <div class="vx-row">
                  <div class="vx-col w-full lg:w-1/2 mb-base">
                    <candidate-civil-state-result
                      :startDate="startDate"
                      :endDate="endDate"
                      :cityId="city_id"
                    />
                  </div>

                  <div class="vx-col w-full lg:w-1/2 mb-base">
                    <candidate-age-study
                      :startDate="startDate"
                      :endDate="endDate"
                      :cityId="city_id"
                    />
                  </div>

                  <div class="vx-col w-full lg:w-1/2 mb-base">
                    <candidate-by-occupation
                      :startDate="startDate"
                      :endDate="endDate"
                      :cityId="city_id"
                    />
                  </div>
                </div>
              </div>
            </vs-tab>

            <!-- Detras del interruptor por cliente mientras la medicion se
                 termina de acordar. El endpoint lo vuelve a comprobar. -->
            <vs-tab label="Tiempos y ANS" v-if="verTiemposYAns">
              <sla-tab :startDate="startDate" :endDate="endDate" />
            </vs-tab>
          </vs-tabs>
        </vx-card>
      </div>

      <div v-else>No existe información</div>
    </div>
  </div>
</template>

<script>
import api from "../mixins/api";
import cities from "../mixins/cities";
import { eventBus } from "./../main";

import vSelect from "vue-select";
import Datepicker from "vuejs-datepicker";
import * as lang from "vuejs-datepicker/src/locale";

import BiChart from "./../components-globalwork/BiChart.vue";
import CandidateYearsExperience from "./../components-globalwork/metrics/candidate_years_experience.vue";
import CandidateAgeStudy from "./../components-globalwork/metrics/candidate_age_study.vue";
import CandidateCivilStateResult from "./../components-globalwork/metrics/candidate_civil_state_result.vue";
import CandidateAgeCivilState from "./../components-globalwork/metrics/candidate_age_civil_state.vue";
import CandidateAgeResult from "./../components-globalwork/metrics/candidate_age_result.vue";
import CandidateCivilStateGender from "./../components-globalwork/metrics/candidate_civil_state_gender.vue";
import CandidateGenderAge from "./../components-globalwork/metrics/candidate_gender_age.vue";

import ProjectsByAdvance from "./../components-globalwork/metrics/projects_by_advance";
import QualificationAverage from "./../components-globalwork/metrics/qualification_average_by_project_type.vue";
import ProjectsByResults from "./../components-globalwork/metrics/projects_by_results.vue";
import ProjectsByType from "./../components-globalwork/metrics/projects_by_type.vue";
import ProjectsByCharge from "./../components-globalwork/metrics/projects_by_charge.vue";

import BannerClients from "./../components-globalwork/metrics/banner_clients.vue";
import UntrustProjects from "./../components-globalwork/metrics/untrust_projects.vue";
import UnreliableProjects from "./../components-globalwork/metrics/unreliable_projects.vue";

import { mapGetters } from "vuex";
import VxCard from "../components/vx-card/VxCard.vue";

import PieChart from "../components-globalwork/metrics/pie_chart.vue";

import ColumnChart from "../components-globalwork/ColumnChart.vue";
import ProjectsByAvgCompletionTimeByType from "./../components-globalwork/metrics/projects_by_avg_completion_time_by_type.vue";
import CandidateByOccupation from "./../components-globalwork/metrics/candidate_by_occupation.vue";
import SlaTab from "./../components-globalwork/sla/SlaTab.vue";

export default {
  mixins: [api, cities],

  data() {
    const today = new Date();
    const oneWeekAgo = new Date();
    oneWeekAgo.setDate(today.getDate() - 7);

    return {
      language: "es",
      languages: lang,
      totalCount: 0,

      startDateMask: oneWeekAgo,
      endDateMask: today,

      startDate: null,
      endDate: null,

      selectedCountryId: null,
      selectedCityId: null,
    };
  },

  computed: {
    ...mapGetters(["userInfo"]),

    verTiemposYAns() {
      return !!(this.userInfo && this.userInfo.sla_metrics_access);
    },

    countryOptions() {
      const list = Array.isArray(this.countries) ? this.countries : [];
      return list.map((c) => ({ id: c.id, label: c.name }));
    },

    cityOptions() {
      const list = Array.isArray(this.cities) ? this.cities : [];
      return list.map((c) => ({ id: c.id, label: c.name }));
    },
  },

  methods: {
    formatDateLocal(dateObj) {
      if (!(dateObj instanceof Date)) return null;
      const yyyy = dateObj.getFullYear();
      const mm = String(dateObj.getMonth() + 1).padStart(2, "0");
      const dd = String(dateObj.getDate()).padStart(2, "0");
      return `${yyyy}-${mm}-${dd}`;
    },

    syncDates() {
      this.startDate = this.formatDateLocal(this.startDateMask);
      this.endDate = this.formatDateLocal(this.endDateMask);
    },

    // El resto de la app arma sus URLs como this.url() + "/metrics/...", asi
    // que VUE_APP_ROOT_API va sin barra final. Aca se concatenaba sin
    // separador, de modo que con ese mismo valor salia
    // "http://127.0.0.1:3001metrics/..." y el navegador la rechazaba por
    // invalida. De ahi venia el fallo que dejaba el tablero en blanco.
    metricsUrl(path) {
      return `${this.url().replace(/\/+$/, "")}/metrics/${path}`;
    },

    async initializeData() {
      const clientId = (this.userInfo.user || {}).client_id;
      if (!clientId) return;

      await this.requestGetWithoutContent(
        this.metricsUrl(`date-of-first-last-project?client_id=${clientId}`),
        {}
      );
      this.syncDates();
    },

    consultData() {
      if (this.selectedCountryId != null && this.selectedCityId == null) {
        this.$vs.notify({
          color: "warning",
          title: "Falta ciudad",
          text: "Si seleccionas un país, debes seleccionar una ciudad.",
          position: "bottom-left",
        });
        return;
      }

      this.country_id = this.selectedCountryId || null;
      this.city_id = this.selectedCityId || null;

      this.syncDates();
      eventBus.$emit("consultData");

      let params = `&start_date=${this.startDate}&end_date=${this.endDate}`;
      let url = this.metricsUrl(
        `total-projects?client_id=${this.userInfo.user.client_id}${params}`
      );

      if (this.city_id != null) url += "&city_id=" + this.city_id;

      this.getTotalProjects(url);
    },

    async getTotalProjects(url) {
      const request = await this.requestGetWithoutContent(url, {});
      if (request && request.status == 200 && request.data && request.data[0]) {
        this.totalCount = request.data[0].TotalProjects;
      }
    },

    enforceNoDefaultCountry() {
      this.selectedCountryId = null;
      this.selectedCityId = null;

      this.country_id = null;
      this.city_id = null;

      this.cities = [];
    },
  },

  watch: {
    startDateMask() {
      this.syncDates();
    },
    endDateMask() {
      this.syncDates();
    },

    async selectedCountryId(newVal, oldVal) {
      if (newVal == null) {
        this.selectedCityId = null;
        this.country_id = null;
        this.city_id = null;
        this.cities = [];
        return;
      }

      if (oldVal != null && newVal !== oldVal) {
        this.selectedCityId = null;
      }

      this.country_id = newVal;
      this.city_id = null;
      this.cities = [];

      if (typeof this.getCities === "function") {
        await this.getCities();
      }
    },

    selectedCityId(newVal) {
      this.city_id = newVal || null;
    },

    country_id(newVal) {
      if (this.selectedCountryId == null && newVal != null) {
        this.enforceNoDefaultCountry();
      }
    },
  },

  components: {
    Datepicker,
    vSelect,
    "v-select": vSelect,

    "bi-chart": BiChart,
    "candidate-years-experience": CandidateYearsExperience,
    "candidate-age-civil-state": CandidateAgeCivilState,
    "projects-by-results": ProjectsByResults,
    "candidate-age-study": CandidateAgeStudy,
    "candidate-civil-state-result": CandidateCivilStateResult,
    "qualification-average": QualificationAverage,
    "candidate-age-result": CandidateAgeResult,
    "projects-by-advance": ProjectsByAdvance,
    "candidate-civil-state-gender": CandidateCivilStateGender,
    "candidate-gender-age": CandidateGenderAge,
    "projects-by-type": ProjectsByType,
    "projects-by-charge": ProjectsByCharge,
    "banner-clients": BannerClients,
    VxCard,
    "untrust-projects": UntrustProjects,
    "unreliable-projects": UnreliableProjects,
    "pie-chart": PieChart,
    "column-chart": ColumnChart,
    "projects-by-avg-completion-time-by-type": ProjectsByAvgCompletionTimeByType,
    "candidate-by-occupation": CandidateByOccupation,
    "sla-tab": SlaTab,
  },

  async created() {
    if (this.userInfo == null) {
      window.localStorage.clear();
      this.$router.push("/pages/login");
      return;
    }

    // Las fechas van primero y pase lo que pase. Son lo unico que decide si el
    // tablero se dibuja, y antes colgaban de que initializeData() volviera.
    // Resultado: cualquier fallo accesorio -un 500, un userInfo con otra
    // forma, la red- dejaba al cliente con "No existe informacion", que no
    // explica nada y hace parecer que no tiene procesos cuando si los tiene.
    this.syncDates();

    try {
      await this.initializeData();
    } catch (e) {
      console.error("Graphics: initializeData fallo, se sigue con el rango por defecto", e);
    }

    try {
      await this.getCountries();
    } catch (e) {
      console.error("Graphics: getCountries fallo", e);
    }

    this.$nextTick(() => {
      this.enforceNoDefaultCountry();
    });

    const clientId = (this.userInfo.user || {}).client_id;
    if (clientId) {
      this.getTotalProjects(
        this.metricsUrl(
          `total-projects?client_id=${clientId}&start_date=${this.startDate}&end_date=${this.endDate}`
        )
      );
    } else {
      console.error("Graphics: el usuario no trae client_id", this.userInfo);
    }
  },
};
</script>

<style scoped>
/* ✅ FIX: el calendario debe quedar por encima de los tabs */
#dashboard-analytics ::v-deep .vdp-datepicker__calendar {
  position: absolute;
  z-index: 99999;
}

/* ✅ Bloque informativo sobre el filtro */
.gw-filter-hint{
  background: rgba(95, 0, 145, 0.06);
  border: 1px solid rgba(95, 0, 145, 0.12);
  border-left: 4px solid rgba(95, 0, 145, 0.55);
  border-radius: 10px;
  padding: 12px 14px;
  margin: 0 0 12px 0;
}

.gw-filter-hint__title{
  font-weight: 700;
  font-size: 14px;
  color: #3b3b3b;
  margin-bottom: 4px;
}

.gw-filter-hint__text{
  font-size: 13px;
  color: #666;
  line-height: 1.35;
}
</style>
